import { useState } from "react";
import { Megaphone } from "lucide-react";

import { Badge } from "@/modern/components/ui/badge";
import { Button } from "@/modern/components/ui/button";
import {
  Popover,
  PopoverTrigger,
} from "@/modern/components/ui/popover";
import { cn } from "@/modern/utils";

import { ChangelogPopoverContent } from "./ChangelogPopover";
import { useChangelogSeen } from "./useChangelogSeen";

const CHANGELOG_BADGE_MAX = 99;

interface ChangelogBellProps {
  className?: string;
}

/** Temporarily hide What's new until the feature is ready to ship. */
const CHANGELOG_TRIGGER_ENABLED = false;

export function ChangelogBell({ className }: ChangelogBellProps) {
  const {
    unreadCount,
    allVersions,
    seenEntryIds,
    allEntryIds,
    markSeen,
  } = useChangelogSeen();
  const [open, setOpen] = useState(false);
  /** Snapshot of which ids were unseen when the panel opened (for NEW badges). */
  const [unseenAtOpen, setUnseenAtOpen] = useState<string[]>([]);

  if (!CHANGELOG_TRIGGER_ENABLED) {
    return null;
  }

  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen === open) return;

    if (nextOpen) {
      const seen = new Set(seenEntryIds);
      setUnseenAtOpen(allEntryIds.filter((id) => !seen.has(id)));
      setOpen(true);
      return;
    }

    markSeen();
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="icon"
          className={cn(
            "relative size-[44px] shrink-0 rounded-xl shadow-none",
            className
          )}
          aria-label={
            unreadCount > 0 ? `${unreadCount} unread updates` : "What's new"
          }
        >
          <Megaphone className="size-[1.125rem]" />
          {unreadCount > 0 && (
            <Badge
              variant="default"
              aria-hidden
              className="pointer-events-none absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full border-primary bg-primary px-1 text-[10px] font-semibold text-primary-foreground"
            >
              {unreadCount > CHANGELOG_BADGE_MAX
                ? `${CHANGELOG_BADGE_MAX}+`
                : unreadCount}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <ChangelogPopoverContent
        open={open}
        versions={allVersions}
        unseenEntryIds={unseenAtOpen}
        onClose={() => handleOpenChange(false)}
      />
    </Popover>
  );
}
